import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Building2, Users, Mail, BellRing, Download, ArrowUpRight, KeyRound, Trash2 } from "lucide-react";
import { BarChart, Bar, XAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from "recharts";
import BroadcastDialog from "@/components/BroadcastDialog";
import PageHeader, { schoolYear } from "@/components/PageHeader";
import ConfirmDialog from "@/components/ConfirmDialog";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useReference } from "@/lib/useReference";
import api from "@/lib/api";
import RequestAlertBanner from "@/components/admin/RequestAlertBanner";
import AdminNotificationsCard from "@/components/admin/AdminNotificationsCard";

const RANGES = [
  { key: "today", label: "Today" },
  { key: "week", label: "This Week" },
  { key: "7", label: "7 Days" },
  { key: "30", label: "30 Days" },
  { key: "90", label: "90 Days" },
];
const rangeDays = (key) => {
  if (key === "today") return 1;
  if (key === "week") return new Date().getDay() + 1; // Sun=1 … Sat=7 (calendar week to date)
  return Number(key);
};
const rangeLabel = (key) => (RANGES.find((r) => r.key === key) || {}).label || key;
const CAT_LABELS = { choice: "Choice", event: "Event", conference: "Conference", announcement: "Rally Call", approval: "Approval", reminder: "Reminder", other: "Other" };
const CAT_COLORS = { choice: "#0ea5e9", event: "#f59e0b", conference: "#8b5cf6", announcement: "#ec4899", approval: "#10b981", reminder: "#f43f5e", other: "#94a3b8" };

export default function AdminDashboard() {
  const { ref } = useReference();
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const [rangeKey, setRangeKey] = useState("30");
  const range = rangeDays(rangeKey);
  const [analytics, setAnalytics] = useState(null);
  const [fDistrict, setFDistrict] = useState("all");
  const [fCampus, setFCampus] = useState("all");
  const [fTeam, setFTeam] = useState("all");
  const [clearAnalyticsOpen, setClearAnalyticsOpen] = useState(false);
  const [clearingAnalytics, setClearingAnalytics] = useState(false);
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const bump = () => setTick((t) => t + 1);
    window.addEventListener("rpg:badges-refresh", bump);
    return () => window.removeEventListener("rpg:badges-refresh", bump);
  }, []);
  useEffect(() => { api.get("/admin/dashboard").then(({ data }) => setD(data)).catch(() => {}); }, []);

  const loadAnalytics = () => {
    setAnalytics(null);
    api.get(`/admin/notification-analytics?${schoolParams()}`).then(({ data }) => setAnalytics(data)).catch(() => {});
  };
  const clearAnalytics = async () => {
    setClearingAnalytics(true);
    try {
      const { data } = await api.delete("/admin/notification-analytics");
      toast.success(`Analytics cleared — ${data.deleted} metric${data.deleted === 1 ? "" : "s"} deleted`);
      setClearAnalyticsOpen(false);
      loadAnalytics();
    } catch { toast.error("Failed to clear analytics"); }
    finally { setClearingAnalytics(false); }
  };

  const schoolParams = () => {
    const p = new URLSearchParams({ days: String(range) });
    if (fDistrict !== "all") p.set("district_id", fDistrict);
    if (fCampus !== "all") p.set("campus_id", fCampus);
    if (fTeam !== "all") p.set("team_id", fTeam);
    return p.toString();
  };

  useEffect(() => {
    setAnalytics(null);
    api.get(`/admin/notification-analytics?${schoolParams()}`).then(({ data }) => setAnalytics(data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rangeKey, fDistrict, fCampus, fTeam]);
  const n = analytics?.totals;
  const byType = (analytics?.categories || []).map((c) => ({ ...c, label: CAT_LABELS[c.name] || c.name }));

  const campusOpts = ref.campuses.filter((c) => fDistrict === "all" || c.district_id === fDistrict);
  const teamOpts = ref.teams.filter((t) => fCampus === "all" ? (fDistrict === "all" || t.district_id === fDistrict) : t.campus_id === fCampus);

  const fmtDay = (s) => { const [, m, day] = s.split("-"); return `${parseInt(m)}/${parseInt(day)}`; };

  const StatCard = ({ testId, title, icon: Icon, to, accent, stats, delay }) => (
    <Card onClick={() => navigate(to)} data-testid={testId} style={{ animationDelay: `${delay}ms` }}
      className="group animate-fade-in-up cursor-pointer border-slate-200 p-6 shadow-sm transition-all hover:-translate-y-0.5 hover:border-slate-300 hover:shadow-md">
      <div className="mb-5 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <span className={`flex h-11 w-11 items-center justify-center rounded-xl ${accent}`}><Icon className="h-5 w-5" /></span>
          <h3 className="font-heading text-lg font-bold text-slate-900">{title}</h3>
        </div>
        <ArrowUpRight className="h-5 w-5 text-slate-300 transition-colors group-hover:text-slate-500" />
      </div>
      <div className="flex flex-wrap justify-evenly gap-x-4 gap-y-5">
        {stats.map((s) => (
          <div key={s.label} className="w-32 text-center" data-testid={s.testId}>
            {s.to ? (
              <button onClick={(e) => { e.stopPropagation(); navigate(s.to); }} data-testid={`${s.testId}-link`}
                className="font-heading text-3xl font-extrabold tabular-nums text-slate-900 transition-colors hover:text-sky-600">
                {s.value ?? "—"}
              </button>
            ) : (
              <p className="font-heading text-3xl font-extrabold tabular-nums text-slate-900">{s.value ?? "—"}</p>
            )}
            <p className="mt-0.5 text-xs font-semibold uppercase tracking-wide text-slate-400">{s.label}</p>
            {s.hint ? (s.hintTo ? (
              <button onClick={(e) => { e.stopPropagation(); navigate(s.hintTo); }} data-testid={`${s.testId}-pending-link`}
                className="mt-0.5 inline-flex items-center gap-0.5 text-[11px] font-semibold text-amber-600 transition-colors hover:text-amber-700 hover:underline">
                {s.hint} <ArrowUpRight className="h-3 w-3" />
              </button>
            ) : <p className="mt-0.5 text-[11px] font-semibold text-amber-600">{s.hint}</p>) : null}
          </div>
        ))}
      </div>
    </Card>
  );

  const exportCsv = async () => {
    try {
      const res = await api.get(`/admin/notification-analytics/export?${schoolParams()}`, { responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(res.data);
      a.download = `notifications_${range}d.csv`;
      document.body.appendChild(a); a.click(); a.remove();
    } catch { /* ignore */ }
  };

  return (
    <div>
      <RequestAlertBanner tick={tick} />
      <PageHeader title="System Administrator Dashboard" subtitle={d ? `RallyPointGo · Active year ${schoolYear(d.active_year)}` : "Loading…"}
        actions={<BroadcastDialog variant="admin" />} />
      <div className="grid grid-cols-1 gap-5">
        <StatCard testId="admin-card-organization" title="Organization" icon={Building2} to="/admin/organization"
          accent="bg-sky-50 text-sky-600" delay={0} stats={[
            { label: "Districts", value: d?.districts, testId: "admin-stat-districts", to: "/admin/organization?tab=districts" },
            { label: "Campuses", value: d?.campuses, testId: "admin-stat-campuses", to: "/admin/organization?tab=campuses" },
            { label: "Teams", value: d?.teams, testId: "admin-stat-teams", to: "/admin/organization?tab=teams" },
          ]} />
        <StatCard testId="admin-card-users" title="Users" icon={Users} to="/admin/accounts"
          accent="bg-violet-50 text-violet-600" delay={80} stats={[
            { label: "Coaches", value: d?.coaches, testId: "admin-stat-coaches", to: "/admin/accounts?role=coach" },
            { label: "Team Members", value: d?.team_members, testId: "admin-stat-members", to: "/admin/accounts?role=team_member", hint: d && d.pending_members ? `${d.pending_members} pending` : "", hintTo: "/admin/accounts?role=team_member&status=pending" },
            { label: "Parents", value: d?.parents, testId: "admin-stat-parents", to: "/admin/accounts?role=parent", hint: d && d.pending_parents ? `${d.pending_parents} pending` : "", hintTo: "/admin/accounts?role=parent&status=pending" },
            { label: "District Admins", value: d?.district_admins, testId: "admin-stat-district-admins", to: "/admin/accounts?role=district_administrator" },
            { label: "Campus Admins", value: d?.campus_admins, testId: "admin-stat-campus-admins", to: "/admin/accounts?role=campus_administrator" },
          ]} />
      </div>

      <Card onClick={() => navigate("/admin/settings?focus=licenses")} className="mt-5 cursor-pointer border-slate-200 p-5 shadow-sm transition-shadow hover:shadow-md" data-testid="admin-feature-adoption">
        <div className="mb-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-indigo-50 text-indigo-600"><KeyRound className="h-4 w-4" /></span>
            <div>
              <p className="font-heading text-sm font-semibold text-slate-800">Feature Adoption</p>
              <p className="text-xs text-slate-400">Teams by license plan — tap a plan to see its teams in License Management</p>
            </div>
          </div>
          <ArrowUpRight className="h-4 w-4 text-slate-400" />
        </div>
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-5">
          {[
            { key: "base", label: "Base", dot: "bg-slate-400", text: "text-slate-700" },
            { key: "standard", label: "Standard", dot: "bg-emerald-500", text: "text-emerald-700" },
            { key: "full", label: "Full", dot: "bg-yellow-400", text: "text-yellow-700" },
            { key: "theatre", label: "Theatre", dot: "bg-rose-500", text: "text-rose-700" },
            { key: "custom", label: "Custom", dot: "bg-blue-500", text: "text-blue-700" },
          ].map((t) => (
            <button type="button" key={t.key} onClick={(e) => { e.stopPropagation(); navigate(`/admin/settings?focus=licenses&plan=${t.key}`); }}
              className="group/plan rounded-lg border border-slate-100 bg-slate-50/60 p-3 text-left transition-colors hover:border-slate-300 hover:bg-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-400"
              data-testid={`admin-plan-${t.key}`} aria-label={`Show ${t.label} plan teams`}>
              <div className="flex items-center gap-1.5">
                <span className={`h-2 w-2 rounded-full ${t.dot}`} />
                <span className="text-xs font-medium text-slate-500">{t.label}</span>
                <ArrowUpRight className="ml-auto h-3.5 w-3.5 text-slate-300 transition-colors group-hover/plan:text-slate-500" />
              </div>
              <p className={`mt-1 font-heading text-2xl font-bold ${t.text}`} data-testid={`admin-plan-count-${t.key}`}>{d?.plan_distribution?.[t.key] ?? "—"}</p>
              <p className="text-[11px] text-slate-400">{(d?.plan_distribution?.[t.key] ?? 0) === 1 ? "team" : "teams"}</p>
            </button>
          ))}
        </div>
      </Card>

      <AdminNotificationsCard tick={tick} />

      <Card className="mt-6 border-slate-200 p-5 shadow-sm" data-testid="admin-notification-analytics">
        <div className="mb-4 flex flex-wrap items-center gap-2">
          <BellRing className="h-4 w-4 text-slate-500" />
          <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Notifications Sent</p>
          <div className="ml-auto flex flex-wrap items-center gap-2">
            <Select value={fDistrict} onValueChange={(v) => { setFDistrict(v); setFCampus("all"); setFTeam("all"); }}>
              <SelectTrigger className="h-8 w-36 text-xs" data-testid="analytics-district-filter"><SelectValue placeholder="District" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All districts</SelectItem>
                {ref.districts.map((x) => <SelectItem key={x.id} value={x.id}>{x.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={fCampus} onValueChange={(v) => { setFCampus(v); setFTeam("all"); }}>
              <SelectTrigger className="h-8 w-36 text-xs" data-testid="analytics-campus-filter"><SelectValue placeholder="Campus" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All campuses</SelectItem>
                {campusOpts.map((x) => <SelectItem key={x.id} value={x.id}>{x.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={fTeam} onValueChange={setFTeam}>
              <SelectTrigger className="h-8 w-36 text-xs" data-testid="analytics-team-filter"><SelectValue placeholder="Team" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All teams</SelectItem>
                {teamOpts.map((x) => <SelectItem key={x.id} value={x.id}>{x.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <div className="flex gap-1 rounded-full bg-slate-100 p-0.5" data-testid="analytics-range-toggle">
              {RANGES.map((r) => (
                <button key={r.key} onClick={() => setRangeKey(r.key)} data-testid={`analytics-range-${r.key}`}
                  className={`rounded-full px-3 py-1 text-xs font-semibold transition-colors ${rangeKey === r.key ? "bg-slate-900 text-white" : "text-slate-500 hover:text-slate-800"}`}>{r.label}</button>
              ))}
            </div>
            <button onClick={exportCsv} data-testid="analytics-export-csv"
              className="inline-flex items-center gap-1 rounded-full border border-slate-200 px-3 py-1 text-xs font-semibold text-slate-600 transition-colors hover:bg-slate-50">
              <Download className="h-3.5 w-3.5" /> Export CSV
            </button>
            <button onClick={() => setClearAnalyticsOpen(true)} disabled={clearingAnalytics} data-testid="analytics-clear-btn"
              className="inline-flex items-center gap-1 rounded-full border border-red-200 px-3 py-1 text-xs font-semibold text-red-600 transition-colors hover:bg-red-50 disabled:opacity-50">
              <Trash2 className="h-3.5 w-3.5" /> Clear analytics
            </button>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          <div data-testid="notif-total">
            <p className="text-3xl font-extrabold tabular-nums text-slate-900 font-heading">{n?.total ?? "—"}</p>
            <p className="mt-0.5 text-xs font-semibold uppercase tracking-wide text-slate-400">Total ({rangeLabel(rangeKey)})</p>
          </div>
          <div data-testid="notif-email">
            <p className="flex items-center gap-1.5 text-3xl font-extrabold tabular-nums text-sky-600 font-heading"><Mail className="h-5 w-5" />{n?.email ?? "—"}</p>
            <p className="mt-0.5 text-xs font-semibold uppercase tracking-wide text-slate-400">Email</p>
          </div>
          <div data-testid="notif-push">
            <p className="flex items-center gap-1.5 text-3xl font-extrabold tabular-nums text-violet-600 font-heading"><BellRing className="h-5 w-5" />{n?.push ?? "—"}</p>
            <p className="mt-0.5 text-xs font-semibold uppercase tracking-wide text-slate-400">Push</p>
          </div>
          <div data-testid="notif-sms">
            <p className="text-3xl font-extrabold tabular-nums text-emerald-600 font-heading">{n?.sms ?? "—"}</p>
            <p className="mt-0.5 text-xs font-semibold uppercase tracking-wide text-slate-400">SMS</p>
          </div>
        </div>

        <div className="mt-5 grid grid-cols-1 gap-4 lg:grid-cols-3">
          <div className="h-48 w-full lg:col-span-2" data-testid="analytics-chart">
            {analytics && (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={analytics.series} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                  <XAxis dataKey="date" tickFormatter={fmtDay} tick={{ fontSize: 11, fill: "#94a3b8" }} interval="preserveStartEnd" minTickGap={24} axisLine={false} tickLine={false} />
                  <Tooltip labelFormatter={fmtDay} cursor={{ fill: "#f1f5f9" }}
                    contentStyle={{ borderRadius: 12, border: "1px solid #e2e8f0", fontSize: 12 }} />
                  <Bar dataKey="email" stackId="a" fill="#0284c7" name="Email" />
                  <Bar dataKey="push" stackId="a" fill="#7c3aed" name="Push" />
                  <Bar dataKey="sms" stackId="a" fill="#059669" name="SMS" radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
          <div className="h-48 w-full" data-testid="analytics-category-chart">
            <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-400">By alert type</p>
            {analytics && (byType.some((c) => c.count > 0) ? (
              <ResponsiveContainer width="100%" height="90%">
                <PieChart>
                  <Pie data={byType} dataKey="count" nameKey="label" cx="40%" cy="50%" innerRadius={34} outerRadius={58} paddingAngle={2}>
                    {byType.map((c) => <Cell key={c.name} fill={CAT_COLORS[c.name]} />)}
                  </Pie>
                  <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #e2e8f0", fontSize: 12 }} />
                  <Legend layout="vertical" align="right" verticalAlign="middle" iconType="circle" wrapperStyle={{ fontSize: 11 }} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex h-[90%] items-center justify-center text-xs text-slate-400">No alerts in this range</div>
            ))}
          </div>
        </div>

        <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="rounded-xl border border-slate-200 p-4" data-testid="analytics-email-breakdown">
            <p className="mb-2 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-sky-600"><Mail className="h-4 w-4" /> Email by type</p>
            <div className="space-y-1.5">
              {(analytics?.email_types || []).map((t) => (
                <div key={t.key} className="flex items-center justify-between text-sm" data-testid={`analytics-email-type-${t.key}`}>
                  <span className="text-slate-600">{t.label}</span>
                  <span className="font-bold tabular-nums text-slate-900">{t.count}</span>
                </div>
              ))}
              {!analytics && <p className="text-xs text-slate-400">Loading…</p>}
            </div>
          </div>
          <div className="rounded-xl border border-slate-200 p-4" data-testid="analytics-push-breakdown">
            <p className="mb-2 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-violet-600"><BellRing className="h-4 w-4" /> Push by type</p>
            <div className="space-y-1.5">
              {(analytics?.push_types || []).map((t) => (
                <div key={t.key} className="flex items-center justify-between text-sm" data-testid={`analytics-push-type-${t.key}`}>
                  <span className="text-slate-600">{t.label}</span>
                  <span className="font-bold tabular-nums text-slate-900">{t.count}</span>
                </div>
              ))}
              {!analytics && <p className="text-xs text-slate-400">Loading…</p>}
            </div>
          </div>
        </div>
      </Card>
      <ConfirmDialog
        open={clearAnalyticsOpen}
        onOpenChange={(o) => !o && setClearAnalyticsOpen(false)}
        title="Clear all notification analytics?"
        description="This permanently deletes every notification analytics metric to start clean. This cannot be undone. An audit-log entry will record that you performed this wipe."
        confirmText={clearingAnalytics ? "Clearing…" : "Clear analytics"}
        destructive
        onConfirm={clearAnalytics}
      />
    </div>
  );
}
